/** A flag do feature-flag-service (`currentFeatures(system: USER_APP)`) que libera o rateio de
 *  centro de custo em várias linhas no checkout, a mesma do app legado
 *  (`AllocationFieldListView`). Sem ela o checkout aceita uma linha só. */
export const COST_CENTER_SPLIT_FEATURE_FLAGS = ['allow_many_allocations'] as const;

/** O teto de linhas do rateio de um viajante, como no legado (`_kMaxAllocation`). */
export const MAX_COST_CENTER_SPLIT_ROWS = 10;

/** Uma linha do rateio, reduzida ao que a regra confere. */
export interface CostCenterSplitRow {
  readonly costCenterId: string;
  /** Ausente vale 100 só numa linha sozinha: o envio de uma linha nunca exigiu percentual. */
  readonly percent?: number | undefined;
}

export type CostCenterSplitIssue =
  /** Mais linhas que o teto, ou mais de uma sem a flag. */
  | 'too_many_rows'
  /** Uma linha sem percentual, com 0% ou abaixo, ou com mais de 2 casas decimais. */
  | 'invalid_percent'
  /** A soma não fecha 100%. */
  | 'sum_not_100'
  /** O mesmo centro de custo em duas linhas (o legado aceitava; a #6697 não). */
  | 'repeated_cost_center';

/** Percentual em centésimos inteiros, ou `undefined` quando tem mais de 2 casas. */
function toCents(percent: number): number | undefined {
  const cents = Math.round(percent * 100);
  return Math.abs(cents - percent * 100) < 1e-6 ? cents : undefined;
}

function rowCents(row: CostCenterSplitRow, alone: boolean): number | undefined {
  if (row.percent === undefined) return alone ? 10_000 : undefined;
  const cents = toCents(row.percent);
  return cents !== undefined && cents > 0 ? cents : undefined;
}

/**
 * O que invalida o rateio de um viajante, ou `undefined` quando ele vale. Lista vazia não é
 * problema desta regra: a presença do rateio é conferida por quem o exige.
 *
 * A soma é conferida em centésimos inteiros, porque `33.33 + 33.33 + 33.34` em ponto
 * flutuante não dá exatamente 100. O `bus-api` do legado recusa a soma abaixo de 100
 * (`check.cost.centers.percent.ts`).
 */
export function costCenterSplitIssue(
  rows: readonly CostCenterSplitRow[],
  options: { allowManyRows: boolean },
): CostCenterSplitIssue | undefined {
  if (rows.length === 0) return undefined;
  const maxRows = options.allowManyRows ? MAX_COST_CENTER_SPLIT_ROWS : 1;
  if (rows.length > maxRows) return 'too_many_rows';
  const cents = rows.map((row) => rowCents(row, rows.length === 1));
  if (cents.some((value) => value === undefined)) return 'invalid_percent';
  const total = cents.reduce<number>((sum, value) => sum + (value ?? 0), 0);
  if (total !== 10_000) return 'sum_not_100';
  const ids = rows.map((row) => row.costCenterId).filter((id) => id !== '');
  return new Set(ids).size === ids.length ? undefined : 'repeated_cost_center';
}
